import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Send, Users, Loader, UserMinus, Flag, X, Shield, Camera, Paperclip, Image as ImageIcon, Mic, Trash2, Pause, Download, ChevronRight, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CABIN_IMG = "https://customer-assets-0z36b82j.emergentagent.net/job_comunidad-duelo/artifacts/x78z4yvw_IMG_6263.jpeg";
const CHAT_BG = { background: `linear-gradient(rgba(13,9,6,0.55), rgba(13,9,6,0.70)), url(${CABIN_IMG}) center/cover no-repeat` };

const WELCOME_KEY = "refugium_room_welcomed_";

function WelcomeDialog({ isAcompanado, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFAF4] rounded-3xl max-w-md w-full p-6 animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full bg-[#6B9070]/10 flex items-center justify-center mx-auto mb-4">
          <Shield size={24} className="text-[#6B9070]" />
        </div>
        <h3 className="font-heading text-xl text-[#5B3E2E] text-center mb-3">Bienvenido a tu Sala Refugium</h3>
        <p className="text-sm text-[#5B3E2E]/70 leading-relaxed mb-4">
          Refugium es un espacio de <strong className="text-[#5B3E2E]">respeto, escucha y acompañamiento</strong>. Te pedimos que hables desde la empatía y cuides de quienes comparten este espacio contigo.
        </p>
        <div data-testid="welcome-confidentiality" className="p-3 bg-[#E48D57]/8 border border-[#E48D57]/25 rounded-xl mb-4 flex gap-2.5">
          <Lock size={16} className="text-[#D07C46] shrink-0 mt-0.5" />
          <p className="text-sm text-[#5B3E2E]/80 leading-relaxed">
            <strong className="text-[#5B3E2E]">Todo lo que se comparte en esta sala es confidencial.</strong> No hagas capturas ni grabaciones de pantalla, ni compartas fuera de Refugium mensajes, fotos, audios o datos de otras personas.
          </p>
        </div>
        {isAcompanado && (
          <div className="p-3 bg-[#6B9070]/5 rounded-xl mb-4">
            <p className="text-sm text-[#6B9070] leading-relaxed">
              Como persona acompañada, puedes <strong>retirar de la sala a cualquier acompañante</strong> si lo consideras necesario, por el motivo que sea. Tu bienestar es lo primero.
            </p>
          </div>
        )}
        <Button onClick={onClose} className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3" data-testid="welcome-dismiss">
          Entendido
        </Button>
      </div>
    </div>
  );
}

function UserProfileModal({ userId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/users/${userId}/profile`, { withCredentials: true })
      .then(r => { if (!cancelled) setProfile(r.data); })
      .catch(() => { if (!cancelled) setProfile(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const avatarSrc = profile?.avatar_url
    ? (profile.avatar_url.startsWith("http") ? profile.avatar_url : `${BACKEND_URL}${profile.avatar_url}`)
    : null;
  const roleLabel = profile?.is_admin
    ? "Equipo Refugium"
    : profile?.role === "acompanado" ? "Acompañad@"
    : profile?.role === "acompanante" ? "Acompañante" : "Miembro";
  const roleBg = profile?.is_admin ? "bg-[#F0A82C]" : profile?.role === "acompanado" ? "bg-[#6B9070]" : "bg-[#E48D57]";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="user-profile-modal" className="bg-[#FDFAF4] rounded-t-3xl sm:rounded-3xl max-w-sm w-full max-h-[90dvh] overflow-y-auto animate-slide-up pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#5B3E2E]/8">
          <h3 className="font-heading text-base text-[#5B3E2E]">Perfil</h3>
          <button data-testid="user-profile-close" onClick={onClose} className="text-[#5B3E2E]/40 hover:text-[#5B3E2E]"><X size={20} /></button>
        </div>
        {loading ? (
          <div className="py-10 flex justify-center text-[#5B3E2E]/50 text-sm">Cargando…</div>
        ) : !profile ? (
          <div className="py-10 text-center text-[#5B3E2E]/50 text-sm">No se pudo cargar el perfil</div>
        ) : (
          <div className="px-6 py-6 flex flex-col items-center text-center">
            <div className={`w-24 h-24 rounded-full overflow-hidden flex items-center justify-center text-white text-3xl font-heading mb-4 ${avatarSrc ? "" : roleBg}`}>
              {avatarSrc ? (
                <img src={avatarSrc} alt={profile.username} className="w-full h-full object-cover" />
              ) : (
                <span>{profile.username?.charAt(0)?.toUpperCase() || "?"}</span>
              )}
            </div>
            <p data-testid="user-profile-username" className="font-heading text-xl text-[#5B3E2E]">{profile.username}</p>
            <div className="mt-1.5 flex items-center gap-1.5">
              {profile.is_admin && <Shield size={12} className="text-[#F0A82C]" />}
              <span className={`text-[11px] uppercase tracking-wider font-semibold ${profile.is_admin ? "text-[#F0A82C]" : "text-[#5B3E2E]/50"}`}>
                {roleLabel}
              </span>
            </div>
            <div className="mt-5 w-full">
              {profile.bio ? (
                <div className="bg-[#5B3E2E]/[0.04] rounded-2xl px-4 py-3 text-left">
                  <p className="text-[10px] uppercase tracking-wider text-[#5B3E2E]/40 font-semibold mb-1">Sobre {profile.username}</p>
                  <p data-testid="user-profile-bio" className="text-sm text-[#5B3E2E] whitespace-pre-wrap break-words leading-relaxed">{profile.bio}</p>
                </div>
              ) : (
                <p className="text-xs text-[#5B3E2E]/40 italic">Este usuario aún no ha añadido una biografía.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MembersPanel({ members, room, user, roomId, onClose, onKick }) {
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const isAcompanado = room?.acompanado?.user_id === user?.id;
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";

  const handleKick = async (targetId, targetName) => {
    if (!window.confirm(`¿Seguro que quieres retirar a ${targetName} de la sala?`)) return;
    try {
      await axios.post(`${API}/rooms/${roomId}/kick/${targetId}`, {}, { withCredentials: true });
      toast.success(`${targetName} ha sido retirado de la sala`);
      onKick();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al expulsar");
    }
  };

  const handleReport = async () => {
    if (!reportTarget || !reportReason.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/rooms/${roomId}/report`, { reported_user_id: reportTarget.user_id, reason: reportReason.trim() }, { withCredentials: true });
      toast.success("Reporte enviado. El equipo de Refugium lo revisará.");
      setReportTarget(null);
      setReportReason("");
    } catch (err) {
      toast.error("Error al enviar el reporte");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-[#FDFAF4] rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[85dvh] overflow-y-auto animate-slide-up pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#5B3E2E]/8">
          <h3 className="font-heading text-lg text-[#5B3E2E]">Participantes <span className="text-[#5B3E2E]/40 text-sm font-sans">({members.length})</span></h3>
          <button data-testid="members-close" onClick={onClose} className="text-[#5B3E2E]/40 hover:text-[#5B3E2E]"><X size={20} /></button>
        </div>

        {reportTarget ? (
          <div className="p-5 space-y-3">
            <p className="text-sm text-[#5B3E2E] font-medium">Reportar a {reportTarget.username}</p>
            <Textarea
              data-testid="report-reason"
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder="Describe el motivo del reporte..."
              className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] min-h-[80px] text-sm"
            />
            <div className="flex gap-2">
              <Button onClick={() => setReportTarget(null)} variant="outline" className="flex-1 rounded-full text-sm border-[#5B3E2E]/15">Cancelar</Button>
              <Button onClick={handleReport} disabled={!reportReason.trim() || submitting} className="flex-1 rounded-full text-sm bg-[#D9534F] hover:bg-[#c9453f] text-white">
                {submitting ? "Enviando..." : "Enviar reporte"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-1">
            {members.map(m => {
              const isSelf = m.user_id === user?.id;
              const canKick = (isAcompanado || isAdmin) && m.role === "acompanante" && !isSelf;
              const canReport = !isSelf;
              return (
                <div key={m.user_id} className="flex items-center justify-between p-3 rounded-xl hover:bg-[#5B3E2E]/5 transition-colors">
                  <button
                    data-testid={`member-row-${m.user_id}`}
                    onClick={() => setProfileUserId(m.user_id)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className={`w-9 h-9 rounded-full overflow-hidden flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${m.avatar_url ? "" : (m.role === "acompanado" ? "bg-[#6B9070]" : "bg-[#E48D57]")}`}>
                      {m.avatar_url ? (
                        <img
                          src={m.avatar_url.startsWith("http") ? m.avatar_url : `${BACKEND_URL}${m.avatar_url}`}
                          alt={m.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        m.username?.charAt(0)?.toUpperCase() || "?"
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-[#5B3E2E] font-medium truncate">{m.username}{isSelf && " (tú)"}</p>
                      <p className="text-[10px] text-[#5B3E2E]/40">{m.role === "acompanado" ? "Acompañad@" : "Acompañante"}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!isSelf && canReport && (
                      <button data-testid={`report-${m.user_id}`} onClick={() => setReportTarget(m)} className="p-2 rounded-full text-[#5B3E2E]/30 hover:text-[#F0A82C] hover:bg-[#F0A82C]/10 transition-colors" title="Reportar">
                        <Flag size={15} />
                      </button>
                    )}
                    {!isSelf && canKick && (
                      <button data-testid={`kick-${m.user_id}`} onClick={() => handleKick(m.user_id, m.username)} className="p-2 rounded-full text-[#5B3E2E]/30 hover:text-[#D9534F] hover:bg-[#D9534F]/10 transition-colors" title="Retirar de la sala">
                        <UserMinus size={15} />
                      </button>
                    )}
                    {isSelf && (
                      <ChevronRight size={14} className="text-[#5B3E2E]/25" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {profileUserId && (
        <UserProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} />
      )}
    </div>
  );
}

export default function RoomChatPage() {
  const { id: roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMembers, setShowMembers] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [typingUsers, setTypingUsers] = useState({}); // { user_id: username }
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const notifPermRef = useRef(false);
  const typingSentRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const typingClearTimersRef = useRef({});

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(p => { notifPermRef.current = p === "granted"; });
    } else if ("Notification" in window) {
      notifPermRef.current = Notification.permission === "granted";
    }
  }, []);

  useEffect(() => {
    if (!roomId) return;
    const markRead = () => axios.post(`${API}/notifications/read/${roomId}`, {}, { withCredentials: true }).catch(() => {});
    markRead();
    const interval = setInterval(markRead, 10000);
    return () => clearInterval(interval);
  }, [roomId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/rooms/${roomId}/members`, { withCredentials: true });
      setMembers(data);
    } catch {}
  }, [roomId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [roomRes, msgsRes] = await Promise.all([
          axios.get(`${API}/rooms/${roomId}`, { withCredentials: true }),
          axios.get(`${API}/rooms/${roomId}/messages`, { withCredentials: true }),
        ]);
        setRoom(roomRes.data);
        setMessages(msgsRes.data);
        fetchMembers();
        // Show welcome dialog once per room
        const welcomeKey = WELCOME_KEY + roomId;
        if (!localStorage.getItem(welcomeKey)) {
          setShowWelcome(true);
          localStorage.setItem(welcomeKey, "1");
        }
      } catch (err) {
        console.error("Error loading room:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [roomId, fetchMembers]);

  useEffect(() => {
    if (!roomId || !user) return;
    const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/chat/${roomId}`);
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        // Typing indicator (ephemeral, not added to messages)
        if (msg.type === "typing") {
          if (msg.user_id === user?.id) return;
          if (msg.is_typing) {
            setTypingUsers(prev => ({ ...prev, [msg.user_id]: msg.username }));
            // Auto-clear after 4s if no further signal arrives
            if (typingClearTimersRef.current[msg.user_id]) {
              clearTimeout(typingClearTimersRef.current[msg.user_id]);
            }
            typingClearTimersRef.current[msg.user_id] = setTimeout(() => {
              setTypingUsers(prev => {
                const next = { ...prev };
                delete next[msg.user_id];
                return next;
              });
            }, 4000);
          } else {
            setTypingUsers(prev => {
              const next = { ...prev };
              delete next[msg.user_id];
              return next;
            });
            if (typingClearTimersRef.current[msg.user_id]) {
              clearTimeout(typingClearTimersRef.current[msg.user_id]);
              delete typingClearTimersRef.current[msg.user_id];
            }
          }
          return;
        }
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Clear typing for the user who just sent a message
        if (msg.user_id) {
          setTypingUsers(prev => {
            if (!prev[msg.user_id]) return prev;
            const next = { ...prev };
            delete next[msg.user_id];
            return next;
          });
        }
        if (msg.user_id !== user?.id && notifPermRef.current && document.hidden) {
          try {
            new Notification("Refugium - Nuevo mensaje", {
              body: `${msg.username}: ${msg.content.slice(0, 80)}`,
              icon: "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png",
              tag: `room-${roomId}`,
            });
          } catch {}
        }
      };
      ws.onerror = () => startPolling();
      ws.onclose = () => startPolling();
      wsRef.current = ws;
    } catch {
      startPolling();
    }
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      Object.values(typingClearTimersRef.current).forEach(t => clearTimeout(t));
      typingClearTimersRef.current = {};
    };
  }, [roomId, user]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/rooms/${roomId}/messages?limit=50`, { withCredentials: true });
        if (data.length > 0) {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.id));
            const newMsgs = data.filter(m => !ids.has(m.id));
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
          });
        }
      } catch {}
    }, 3000);
  }, [roomId]);

  useEffect(scrollToBottom, [messages, scrollToBottom]);

  const sendTypingSignal = useCallback((isTyping) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    try {
      wsRef.current.send(JSON.stringify({ type: "typing", is_typing: isTyping }));
    } catch {}
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMsg(value);
    if (value.trim().length > 0) {
      if (!typingSentRef.current) {
        sendTypingSignal(true);
        typingSentRef.current = true;
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingSignal(false);
        typingSentRef.current = false;
      }, 2500);
    } else if (typingSentRef.current) {
      sendTypingSignal(false);
      typingSentRef.current = false;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    chatInputRef.current?.focus();
    setSending(true);
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ content: newMsg.trim() }));
      } else {
        const { data } = await axios.post(`${API}/rooms/${roomId}/messages`, { content: newMsg.trim() }, { withCredentials: true });
        setMessages(prev => {
          if (prev.some(m => m.id === data.id)) return prev;
          return [...prev, data];
        });
      }
      setNewMsg("");
      // Clear typing flag after sending
      if (typingSentRef.current) {
        sendTypingSignal(false);
        typingSentRef.current = false;
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    } catch (err) {
      console.error("Send error:", err);
    } finally {
      setSending(false);
    }
  };

  const memberCount = room ? (room.acompanado ? 1 : 0) + (room.acompanantes?.length || 0) : 0;
  const topicLabel = room?.room_name || (room?.topics?.slice(0, 2).join(", ") || "Sala Refugium");
  const isAcompanado = room?.acompanado?.user_id === user?.id;
  const roomImageSrc = room?.image_url
    ? (room.image_url.startsWith("http") ? room.image_url : `${BACKEND_URL}${room.image_url}`)
    : null;
  const roomInitial = topicLabel.charAt(0).toUpperCase();
  const roomAvatarBg = isAcompanado ? "#6B9070" : "#E48D57";

  const handleRoomImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/rooms/${roomId}/image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRoom(prev => prev ? { ...prev, image_url: data.image_url } : prev);
      toast.success("Imagen actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir la imagen");
    }
  };

  // ── Media (chat attachments)
  const [uploading, setUploading] = useState(false);
  const fileInputImageRef = useRef(null);
  const chatInputRef = useRef(null);
  const handleSendRef = useRef(null);
  handleSendRef.current = handleSend;
  // Native non-passive touchstart: send without the input losing focus (keyboard stays open)
  const sendBtnRef = useCallback((el) => {
    if (!el) return;
    el.addEventListener("touchstart", (e) => {
      e.preventDefault();
      handleSendRef.current?.();
    }, { passive: false });
  }, []);
  const fileInputAnyRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [waveSamples, setWaveSamples] = useState([]); // history of mic levels
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const recordingStartRef = useRef(0);
  const pausedAtRef = useRef(0);
  const totalPausedRef = useRef(0);
  const recordingCancelledRef = useRef(false);
  const durationIntervalRef = useRef(null);
  const waveSamplesRef = useRef([]);

  const sendMediaMessage = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("El archivo supera el límite de 200 MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: up } = await axios.post(`${API}/rooms/${roomId}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await axios.post(`${API}/rooms/${roomId}/messages`, {
        content: "",
        media_type: up.media_type,
        media_url: up.url,
        file_name: up.file_name || null,
      }, { withCredentials: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar archivo");
    } finally {
      setUploading(false);
    }
  };

  const getSupportedAudioMime = () => {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = [
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
    ];
    for (const c of candidates) {
      try { if (MediaRecorder.isTypeSupported(c)) return c; } catch {}
    }
    return "";
  };

  const handleStartRecording = async () => {
    if (recording) return;
    // Force-dismiss any focused input (e.g. text field) so the iOS keyboard
    // closes and the page horizontal scroll is reset before the recording
    // overlay mounts. Otherwise the layout can be pushed and buttons clipped.
    try {
      if (typeof document !== "undefined") {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
        // Reset any horizontal scroll that iOS may have caused on focus.
        if (typeof window !== "undefined") {
          window.scrollTo({ left: 0, top: window.scrollY });
        }
      }
    } catch {}
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const ACtx = window.AudioContext || window.webkitAudioContext;
      const ac = new ACtx();
      audioContextRef.current = ac;
      const source = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mime = getSupportedAudioMime();
      const opts = mime ? { mimeType: mime } : {};
      const mr = new MediaRecorder(stream, opts);
      audioChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (audioStreamRef.current) audioStreamRef.current.getTracks().forEach(t => t.stop());
        if (audioContextRef.current) { try { await audioContextRef.current.close(); } catch {} }
        const finalMime = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: finalMime });
        audioChunksRef.current = [];
        waveSamplesRef.current = [];
        const cancelled = recordingCancelledRef.current;
        if (!cancelled && blob.size > 200) {
          const ext = finalMime.includes("mp4") ? "m4a" : finalMime.includes("ogg") ? "ogg" : "webm";
          const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: finalMime });
          await sendMediaMessage(file);
        }
      };
      mediaRecorderRef.current = mr;
      recordingCancelledRef.current = false;
      recordingStartRef.current = Date.now();
      totalPausedRef.current = 0;
      pausedAtRef.current = 0;
      waveSamplesRef.current = [];
      setWaveSamples([]);
      mr.start();
      setRecording(true);
      setPaused(false);
      setRecordingMs(0);
      durationIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - recordingStartRef.current - totalPausedRef.current
          - (pausedAtRef.current ? Date.now() - pausedAtRef.current : 0);
        setRecordingMs(elapsed);
      }, 100);
      // Begin sampling audio level
      const sampleLoop = () => {
        if (!analyserRef.current) return;
        const a = analyserRef.current;
        const data = new Uint8Array(a.frequencyBinCount);
        a.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const avg = sum / data.length / 255;
        if (mediaRecorderRef.current?.state === "recording") {
          waveSamplesRef.current = [...waveSamplesRef.current, avg].slice(-60);
          setWaveSamples([...waveSamplesRef.current]);
        }
        animationRef.current = requestAnimationFrame(sampleLoop);
      };
      animationRef.current = requestAnimationFrame(sampleLoop);
    } catch (err) {
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const handlePauseRecording = () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    if (mr.state === "recording") {
      mr.pause();
      pausedAtRef.current = Date.now();
      setPaused(true);
    } else if (mr.state === "paused") {
      mr.resume();
      if (pausedAtRef.current) totalPausedRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = 0;
      setPaused(false);
    }
  };

  const handleSendRecording = () => {
    recordingCancelledRef.current = false;
    if (durationIntervalRef.current) { clearInterval(durationIntervalRef.current); durationIntervalRef.current = null; }
    setRecording(false);
    setPaused(false);
    setRecordingMs(0);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
  };

  const handleCancelRecording = () => {
    recordingCancelledRef.current = true;
    if (durationIntervalRef.current) { clearInterval(durationIntervalRef.current); durationIntervalRef.current = null; }
    setRecording(false);
    setPaused(false);
    setRecordingMs(0);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
  };

  const formatDuration = (ms) => {
    const total = Math.floor(ms / 1000);
    const mm = Math.floor(total / 60);
    const ss = (total % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-[60vh]"><Loader size={24} className="text-[#E48D57] animate-spin" /></div>;
  }

  return (
    <div data-testid="room-chat-page" className="flex flex-col h-full max-w-lg mx-auto overflow-x-hidden w-full">
      {/* Welcome Dialog */}
      {showWelcome && <WelcomeDialog isAcompanado={isAcompanado} onClose={() => setShowWelcome(false)} />}

      {/* Members Panel */}
      {showMembers && (
        <MembersPanel members={members} room={room} user={user} roomId={roomId} onClose={() => setShowMembers(false)} onKick={fetchMembers} />
      )}

      {profileUserId && (
        <UserProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} />
      )}

      {/* Chat Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#FDFAF4] border-b border-[#5B3E2E]/10">
        <button data-testid="chat-back-btn" onClick={() => navigate("/app/salas")} className="text-[#5B3E2E]/60 hover:text-[#5B3E2E]">
          <ArrowLeft size={20} />
        </button>
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center text-white font-semibold text-sm" style={{ backgroundColor: roomImageSrc ? "transparent" : roomAvatarBg }}>
            {roomImageSrc ? (
              <img src={roomImageSrc} alt={topicLabel} className="w-full h-full object-cover" />
            ) : (
              <span>{roomInitial}</span>
            )}
          </div>
          {isAcompanado && (
            <label data-testid="room-image-upload-label" className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#E48D57] flex items-center justify-center cursor-pointer hover:bg-[#D07C46] transition-colors shadow-[0_2px_4px_rgba(0,0,0,0.2)]" title="Cambiar imagen de la sala">
              <Camera size={11} className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={handleRoomImageUpload} data-testid="room-image-upload" />
            </label>
          )}
        </div>
        <button
          data-testid="room-header-info"
          onClick={() => { fetchMembers(); setShowMembers(true); }}
          className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
        >
          <p className="font-medium text-[#5B3E2E] text-sm truncate font-heading">{topicLabel}</p>
          <p className="text-[10px] text-[#5B3E2E]/40">Toca para ver los {memberCount} participantes</p>
        </button>
        <button data-testid="show-members-btn" onClick={() => { fetchMembers(); setShowMembers(true); }} className="w-8 h-8 rounded-full bg-[#5B3E2E]/8 flex items-center justify-center hover:bg-[#5B3E2E]/15 transition-colors">
          <Users size={16} className="text-[#5B3E2E]/60" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={CHAT_BG}>
        <div data-testid="chat-confidentiality-notice" className="flex justify-center">
          <p className="inline-flex items-center gap-1.5 text-[11px] text-[#5B3E2E]/80 bg-[#FDFAF4]/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-center">
            <Lock size={11} className="shrink-0" /> Sala confidencial · No hagas capturas ni compartas nada fuera de Refugium
          </p>
        </div>
        {/* UI-only hint: only the acompañado of an empty room sees it */}
        {isAcompanado && messages.length === 0 && (
          <div data-testid="acompanado-empty-hint" className="max-w-[85%] mx-auto chat-bubble-system px-4 py-3 text-sm italic text-center">
            Esta es tu Sala Refugium. Empieza la conversación escribiendo lo que te gustaría compartir con la comunidad.
          </div>
        )}
        {messages.map((msg) => {
          const isSystem = msg.type === "system";
          const isSelf = msg.user_id === user?.id;
          if (isSystem) {
            return <div key={msg.id} className="chat-bubble-system px-4 py-3 text-sm max-w-[85%] mx-auto">{msg.content}</div>;
          }
          const avatarSrc = msg.avatar_url
            ? (msg.avatar_url.startsWith("http") ? msg.avatar_url : `${BACKEND_URL}${msg.avatar_url}`)
            : null;
          const initial = (msg.username || "?").charAt(0).toUpperCase();
          const isAcompanadoMsg = room?.acompanado?.user_id === msg.user_id;
          const fallbackBg = isAcompanadoMsg ? "bg-[#6B9070]" : "bg-[#E48D57]";
          const Avatar = (
            <div className={`w-8 h-8 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-white text-xs font-medium ${avatarSrc ? "" : fallbackBg}`}>
              {avatarSrc ? (
                <img src={avatarSrc} alt={msg.username} className="w-full h-full object-cover" />
              ) : (
                <span>{initial}</span>
              )}
            </div>
          );
          return (
            <div key={msg.id} data-testid={`chat-message-${msg.id}`} className={`flex items-end gap-2 ${isSelf ? "justify-end" : "justify-start"}`}>
              {!isSelf && Avatar}
              <div className={`max-w-[75%] ${isSelf ? `chat-bubble-self${isAcompanadoMsg ? " chat-bubble-acompanado" : ""}` : "chat-bubble-other"} ${msg.media_type ? "p-2" : "px-4 py-3"}`}>
                {!isSelf && <button data-testid={`msg-author-${msg.user_id}`} onClick={() => setProfileUserId(msg.user_id)} className={`text-xs font-medium opacity-70 hover:opacity-100 hover:underline ${msg.media_type ? "px-2 pt-1" : "mb-1"}`}>{msg.username}</button>}
                {msg.media_type === "image" && msg.media_url && (
                  <a href={msg.media_url.startsWith("http") ? msg.media_url : `${BACKEND_URL}${msg.media_url}`} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={msg.media_url.startsWith("http") ? msg.media_url : `${BACKEND_URL}${msg.media_url}`} alt="Adjunto" className="max-w-full max-h-[280px] rounded-xl object-cover" loading="lazy" />
                  </a>
                )}
                {msg.media_type === "video" && msg.media_url && (
                  <video controls className="max-w-full max-h-[280px] rounded-xl">
                    <source src={msg.media_url.startsWith("http") ? msg.media_url : `${BACKEND_URL}${msg.media_url}`} />
                  </video>
                )}
                {msg.media_type === "audio" && msg.media_url && (
                  <audio controls src={msg.media_url.startsWith("http") ? msg.media_url : `${BACKEND_URL}${msg.media_url}`} className="max-w-full" />
                )}
                {msg.media_type === "file" && msg.media_url && (
                  <a href={msg.media_url.startsWith("http") ? msg.media_url : `${BACKEND_URL}${msg.media_url}`} download={msg.file_name || true} className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isSelf ? "bg-white/15 hover:bg-white/25" : "bg-[#5B3E2E]/8 hover:bg-[#5B3E2E]/15"} transition-colors`}>
                    <Download size={16} className={isSelf ? "text-white" : "text-[#5B3E2E]"} />
                    <span className={`text-xs truncate max-w-[180px] ${isSelf ? "text-white" : "text-[#5B3E2E]"}`}>{msg.file_name || "Archivo"}</span>
                  </a>
                )}
                {msg.content && (
                  <p className={`text-sm whitespace-pre-wrap break-words ${msg.media_type ? "px-2 pt-1.5" : ""}`}>{msg.content}</p>
                )}
                <p className={`text-[10px] ${msg.media_type ? "px-2 pb-1 pt-0.5" : "mt-1"} ${isSelf ? "text-white/60" : "text-[#5B3E2E]/40"}`}>
                  {new Date(msg.created_at).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              {isSelf && Avatar}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {Object.keys(typingUsers).length > 0 && (
        <div data-testid="typing-indicator" className="px-4 pt-1 pb-1 bg-[#FDFAF4]">
          <div className="flex items-center gap-2 text-[11px] text-[#5B3E2E]/55 italic">
            <span className="flex gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E48D57]/60 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#E48D57]/60 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#E48D57]/60 animate-bounce" style={{ animationDelay: "300ms" }} />
            </span>
            <span>
              {Object.values(typingUsers).length === 1
                ? `${Object.values(typingUsers)[0]} está escribiendo…`
                : `${Object.values(typingUsers).slice(0, 2).join(", ")} están escribiendo…`}
            </span>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-3 bg-[#FDFAF4] border-t border-[#5B3E2E]/10 relative overflow-x-hidden w-full">
        <input ref={fileInputImageRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) sendMediaMessage(f); e.target.value = ""; }} data-testid="chat-image-input" />
        <input ref={fileInputAnyRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) sendMediaMessage(f); e.target.value = ""; }} data-testid="chat-file-input" />

        {recording ? (
          <div
            data-testid="chat-recording-overlay"
            className="grid items-center gap-2 h-11"
            style={{ gridTemplateColumns: "40px 40px minmax(0,1fr) 40px 40px" }}
          >
            {/* Cancel (trash) */}
            <button
              data-testid="chat-cancel-recording-btn"
              onClick={handleCancelRecording}
              className="w-10 h-10 rounded-full bg-[#5B3E2E]/8 hover:bg-[#5B3E2E]/15 text-[#5B3E2E]/70 flex items-center justify-center transition-colors"
              title="Cancelar"
            >
              <Trash2 size={18} />
            </button>

            {/* Timer */}
            <span className="text-xs font-mono text-[#5B3E2E] tabular-nums">{formatDuration(recordingMs)}</span>

            {/* Waveform area (clipped to its grid cell) */}
            <div className="relative h-7 overflow-hidden flex items-center">
              {waveSamples.length === 0 ? (
                <span className="text-[10px] text-[#5B3E2E]/40 italic">Habla al micrófono…</span>
              ) : (
                <div className="absolute inset-y-0 right-0 flex items-center gap-[2px]">
                  {waveSamples.slice(-32).map((v, i) => {
                    const h = Math.max(3, Math.min(28, v * 32 + 3));
                    return (
                      <span
                        key={i}
                        className={`w-[3px] rounded-full ${paused ? "bg-[#5B3E2E]/30" : "bg-[#E48D57]"}`}
                        style={{ height: `${h}px` }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pause/Resume */}
            <button
              data-testid="chat-pause-recording-btn"
              onClick={handlePauseRecording}
              className="w-10 h-10 rounded-full border-2 border-[#c44a3a] text-[#c44a3a] hover:bg-[#c44a3a]/10 flex items-center justify-center transition-colors"
              title={paused ? "Reanudar" : "Pausar"}
            >
              {paused ? <Mic size={16} /> : <Pause size={16} fill="#c44a3a" />}
            </button>

            {/* Send */}
            <button
              data-testid="chat-send-recording-btn"
              onClick={handleSendRecording}
              className="w-10 h-10 rounded-full bg-[#6B9070] hover:bg-[#5A8060] text-white flex items-center justify-center transition-colors shadow-[0_2px_8px_rgba(72,115,76,0.35)]"
              title="Enviar"
            >
              <Send size={16} />
            </button>
          </div>
        ) : (
          <div
            className="grid items-center gap-1.5"
            style={{ gridTemplateColumns: "36px 36px minmax(0,1fr) 40px" }}
          >
            <button data-testid="chat-attach-image-btn" onClick={() => fileInputImageRef.current?.click()} disabled={uploading} className="w-9 h-9 rounded-full hover:bg-[#5B3E2E]/8 flex items-center justify-center text-[#5B3E2E]/60 disabled:opacity-40" title="Imagen o vídeo">
              <ImageIcon size={18} />
            </button>
            <button data-testid="chat-attach-file-btn" onClick={() => fileInputAnyRef.current?.click()} disabled={uploading} className="w-9 h-9 rounded-full hover:bg-[#5B3E2E]/8 flex items-center justify-center text-[#5B3E2E]/60 disabled:opacity-40" title="Archivo">
              <Paperclip size={18} />
            </button>
            <Input
              ref={chatInputRef}
              data-testid="chat-input"
              value={newMsg}
              onChange={handleInputChange}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Escribe un mensaje..."
              className="w-full min-w-0 bg-transparent border-[#5B3E2E]/15 rounded-full text-[#5B3E2E] placeholder:text-[#5B3E2E]/30"
              style={{ fontSize: "16px" }}
            />
            {newMsg.trim() ? (
              <button ref={sendBtnRef} data-testid="chat-send-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleSend} disabled={sending} className="w-10 h-10 rounded-full bg-[#E48D57] flex items-center justify-center text-white hover:bg-[#D07C46] disabled:opacity-40 transition-all">
                <Send size={16} />
              </button>
            ) : (
              <button
                data-testid="chat-record-btn"
                onClick={handleStartRecording}
                disabled={uploading}
                className="w-10 h-10 rounded-full bg-[#E48D57] flex items-center justify-center text-white hover:bg-[#D07C46] disabled:opacity-40 transition-all"
                title="Grabar audio"
              >
                <Mic size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
